import React from "react";
import { cn, getControlKey, isModifierKeyPressed } from "@gettactic/helpers";
import {
  format,
  differenceInMinutes,
  addMinutes,
  setHours,
  setMinutes
} from "date-fns";
import {
  roundToSnap,
  clampToRange,
  isTodayInTimezone,
  getCurrentTimePosition
} from "@cmp-src/calendar/CalendarDayView/helpers";
import { useTimelineDragSelect } from "@gettactic/hooks";
import { ScrollArea } from "@cmp-src/primitives/scroll-area";

export interface TimePreset {
  label: string;
  type?: "absolute" | "relative" | "dynamic";
  startHour?: number;
  startMinute?: number;
  endHour?: number;
  endMinute?: number;
  durationMinutes?: number | null; // For relative presets (from NOW), null = use defaultMeetingDuration
  dynamicType?: "morning" | "afternoon" | "fullday"; // For dynamic presets based on visible range
}

const DEFAULT_SNAP_MINUTES = 5;
const MODIFIER_SNAP_MINUTES = 15;

const DEFAULT_PRESETS: TimePreset[] = [
  { label: "Morning", type: "dynamic", dynamicType: "morning" },
  { label: "Afternoon", type: "dynamic", dynamicType: "afternoon" },
  { label: "Full Day", type: "dynamic", dynamicType: "fullday" },
  { label: "Meeting Now", type: "relative", durationMinutes: null }, // Uses defaultMeetingDuration
  { label: "30 minutes", type: "relative", durationMinutes: 30 },
  { label: "1 hour", type: "relative", durationMinutes: 60 }
];

export interface TimelineRangePickerProps {
  startTime: Date | null;
  endTime: Date | null;
  onStartTimeChange: (time: Date) => void;
  onEndTimeChange: (time: Date) => void;
  /** Called when both start and end times change together (e.g., preset selection).
   *  If provided, this is preferred over calling onStartTimeChange + onEndTimeChange separately. */
  onRangeChange?: (start: Date, end: Date) => void;
  date: Date;
  snapMinutes?: number;
  minDurationMinutes?: number;
  visibleStartHour?: number;
  visibleEndHour?: number;
  height?: number;
  className?: string;
  presets?: TimePreset[];
  showPresets?: boolean;
  /** Office timezone (IANA format, e.g., "America/New_York") for timezone-aware NOW line */
  timeZone?: string;
  /** Default meeting duration in minutes (used for "Meeting Now" preset) */
  defaultMeetingDuration?: number;
  /** Office opening hour (e.g., 9 for 9 AM) - used for "Full Day" preset */
  officeStartHour?: number;
  /** Office closing hour (e.g., 17 for 5 PM) - used for "Full Day" preset */
  officeEndHour?: number;
  /** Enable scrollable mode with larger time scale (default: false) */
  scrollable?: boolean;
  /** Height multiplier for scrollable mode - increases the visual scale (default: 2) */
  scrollableHeightMultiplier?: number;
}

/**
 * Picks a time range on a vertical timeline of one day: drag to draw, move or resize it
 * (Ctrl/⌘ snaps to 15 minutes), use the Quick Select presets, or the keyboard (↑/↓ start,
 * Shift end, Ctrl/⌘ both). Used inside `StartEndTimePicker`.
 */
export const TimelineRangePicker: React.FC<TimelineRangePickerProps> = ({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  onRangeChange,
  date,
  snapMinutes = DEFAULT_SNAP_MINUTES,
  minDurationMinutes = 15,
  visibleStartHour = 0,
  visibleEndHour = 24,
  height = 400,
  className,
  presets = DEFAULT_PRESETS,
  showPresets = true,
  timeZone,
  defaultMeetingDuration = 60,
  officeStartHour = 9,
  officeEndHour = 17,
  scrollable = false,
  scrollableHeightMultiplier = 2
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const scrollViewportRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const [dragType, setDragType] = React.useState<
    "move" | "resize-start" | "resize-end" | null
  >(null);

  // Mobile snap toggle: when true, use modifier snap interval instead of default
  const [mobileSnapEnabled, setMobileSnapEnabled] = React.useState(false);

  // Local state for preview during drag (only applied on release)
  const [pendingStart, setPendingStart] = React.useState<Date | null>(null);
  const [pendingEnd, setPendingEnd] = React.useState<Date | null>(null);

  // Keyboard debounce state
  const [keyboardStart, setKeyboardStart] = React.useState<Date | null>(null);
  const [keyboardEnd, setKeyboardEnd] = React.useState<Date | null>(null);
  const keyboardTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Timer to update NOW line every 60 seconds
  const [currentTime, setCurrentTime] = React.useState(new Date());

  // Auto-focus for keyboard navigation
  React.useEffect(() => {
    wrapperRef.current?.focus();
  }, []);

  // Cleanup keyboard timeout on unmount
  React.useEffect(() => {
    return () => {
      if (keyboardTimeoutRef.current) {
        clearTimeout(keyboardTimeoutRef.current);
      }
    };
  }, []);

  // Update NOW line every 60 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every 60 seconds

    return () => clearInterval(interval);
  }, []);

  const dragRef = React.useRef<{
    startY: number;
    originalStart: Date;
    originalEnd: Date;
    pointerId: number;
  } | null>(null);

  // Calculate total height and pixels per minute based on visible hours
  const VERTICAL_PADDING = 12; // padding at top and bottom
  const totalHours = visibleEndHour - visibleStartHour;
  const totalMinutes = totalHours * 60;
  const containerHeight = height - VERTICAL_PADDING * 2;

  // In scrollable mode, increase the timeline height for better visibility
  const contentHeightMultiplier = scrollable ? scrollableHeightMultiplier : 1;
  const availableHeight = containerHeight * contentHeightMultiplier;
  const totalHeight = availableHeight;
  const pixelsPerMinute = availableHeight / totalMinutes;

  // Auto-scroll to office start hour in scrollable mode
  React.useEffect(() => {
    if (!scrollable || !scrollViewportRef.current) return;

    // Calculate scroll position to show office start hour at top
    const scrollToHour = officeStartHour > 0 ? officeStartHour - 1 : visibleStartHour;
    const scrollPosition = (scrollToHour - visibleStartHour) * 60 * pixelsPerMinute;

    // Small delay to ensure ScrollArea is mounted
    const timeoutId = setTimeout(() => {
      if (scrollViewportRef.current) {
        scrollViewportRef.current.scrollTop = scrollPosition;
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [scrollable, officeStartHour, visibleStartHour, pixelsPerMinute]);

  // Drag-to-select hook for background dragging
  const dragToSelect = useTimelineDragSelect({
    containerRef,
    date,
    snapMinutes,
    modifierSnapMinutes: MODIFIER_SNAP_MINUTES,
    minDurationMinutes,
    pixelsPerMinute,
    visibleStartHour,
    visibleEndHour,
    onRangeChange,
    dragThresholdPx: 3, // Smaller threshold for compact UI
    touchDragThresholdPx: 8, // Larger for touch
    currentStartTime: startTime,
    currentEndTime: endTime,
    containerPaddingTop: VERTICAL_PADDING,
    mobileSnapEnabled
  });

  // Use pending values during drag, keyboard values during keyboard nav, otherwise use props
  const isKeyboardActive = keyboardStart !== null || keyboardEnd !== null;
  const isBgDragging = dragToSelect.isDragging;
  const displayStart =
    isBgDragging && dragToSelect.pendingStart
      ? dragToSelect.pendingStart
      : isDragging && pendingStart
        ? pendingStart
        : isKeyboardActive && keyboardStart
          ? keyboardStart
          : startTime;
  const displayEnd =
    isBgDragging && dragToSelect.pendingEnd
      ? dragToSelect.pendingEnd
      : isDragging && pendingEnd
        ? pendingEnd
        : isKeyboardActive && keyboardEnd
          ? keyboardEnd
          : endTime;

  // Generate hour markers (exclude end hour as it's at the very edge)
  const hourMarkers = React.useMemo(() => {
    const markers: number[] = [];
    for (let h = visibleStartHour; h < visibleEndHour; h++) {
      markers.push(h);
    }
    return markers;
  }, [visibleStartHour, visibleEndHour]);

  // Convert Y position to Date
  const yToDate = React.useCallback(
    (y: number): Date => {
      const minutesFromStart = y / pixelsPerMinute;
      const totalMins = visibleStartHour * 60 + minutesFromStart;
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      d.setMinutes(totalMins);
      return d;
    },
    [date, visibleStartHour, pixelsPerMinute]
  );

  // Convert Date to Y position
  const dateToY = React.useCallback(
    (d: Date): number => {
      const hours = d.getHours();
      const minutes = d.getMinutes();
      const totalMins = hours * 60 + minutes;
      return (totalMins - visibleStartHour * 60) * pixelsPerMinute;
    },
    [visibleStartHour, pixelsPerMinute]
  );

  // Calculate selection block position and height
  const selectionStyle = React.useMemo(() => {
    if (!displayStart || !displayEnd) return null;

    let top = dateToY(displayStart);
    let bottom = dateToY(displayEnd);

    // Check if end time wrapped to next day (end is before start in the same day view)
    // This happens when dragging past midnight
    const endIsNextDay =
      displayEnd < displayStart ||
      displayEnd.getDate() !== displayStart.getDate();

    if (endIsNextDay) {
      // End time is on next day - extend to bottom of timeline
      bottom = totalHeight;
    }

    // Check if start time is before visible range (previous day)
    const startIsPrevDay = top < 0;
    if (startIsPrevDay) {
      top = 0;
    }

    // Clamp visual positions to visible range
    const clampedTop = Math.max(0, Math.min(top, totalHeight));
    const clampedBottom = Math.max(0, Math.min(bottom, totalHeight));

    // Calculate height from clamped positions
    let blockHeight = clampedBottom - clampedTop;

    // If block is completely outside visible range, don't show it
    if (clampedTop >= totalHeight || clampedBottom <= 0 || blockHeight <= 0) {
      return null;
    }

    return {
      top: clampedTop,
      height: blockHeight,
      extendsToNextDay: endIsNextDay,
      extendsToPrevDay: startIsPrevDay
    };
  }, [displayStart, displayEnd, dateToY, totalHeight]);

  // Handle pointer down on selection block (drag to move)
  const handleSelectionPointerDown = (
    e: React.PointerEvent<HTMLDivElement>
  ) => {
    if (!startTime || !endTime) return;

    e.stopPropagation();
    e.preventDefault();

    const target = e.target as HTMLElement;
    const isTopHandle = target.dataset.handle === "top";
    const isBottomHandle = target.dataset.handle === "bottom";

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

    dragRef.current = {
      startY: e.clientY,
      originalStart: startTime,
      originalEnd: endTime,
      pointerId: e.pointerId
    };

    // Initialize pending values with current values
    setPendingStart(startTime);
    setPendingEnd(endTime);
    setIsDragging(true);

    if (isTopHandle) {
      setDragType("resize-start");
    } else if (isBottomHandle) {
      setDragType("resize-end");
    } else {
      setDragType("move");
    }
  };

  // Handle pointer move during drag
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging || !dragRef.current || !dragType) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Check for modifier key OR mobile toggle to adjust snap interval
    const modifierPressed = isModifierKeyPressed(e);
    const effectiveSnap =
      modifierPressed || mobileSnapEnabled
        ? MODIFIER_SNAP_MINUTES
        : snapMinutes;

    const deltaY = e.clientY - dragRef.current.startY;
    const deltaMinutes = deltaY / pixelsPerMinute;

    if (dragType === "move") {
      // Move entire block - times can go beyond visible range (next/prev day)
      let newStart = addMinutes(dragRef.current.originalStart, deltaMinutes);
      let newEnd = addMinutes(dragRef.current.originalEnd, deltaMinutes);

      newStart = roundToSnap(newStart, effectiveSnap);
      newEnd = roundToSnap(newEnd, effectiveSnap);

      // Don't clamp - allow times to go beyond visible range
      // Visual clamping is handled in selectionStyle

      setPendingStart(newStart);
      setPendingEnd(newEnd);
    } else if (dragType === "resize-start") {
      // Resize from top
      let newStart = addMinutes(dragRef.current.originalStart, deltaMinutes);
      newStart = roundToSnap(newStart, effectiveSnap);
      newStart = clampToRange(newStart, date, visibleStartHour, visibleEndHour);

      // Enforce minimum duration
      const maxStart = addMinutes(
        dragRef.current.originalEnd,
        -minDurationMinutes
      );
      if (newStart > maxStart) {
        newStart = maxStart;
      }

      setPendingStart(newStart);
    } else if (dragType === "resize-end") {
      // Resize from bottom
      let newEnd = addMinutes(dragRef.current.originalEnd, deltaMinutes);
      newEnd = roundToSnap(newEnd, effectiveSnap);
      newEnd = clampToRange(newEnd, date, visibleStartHour, visibleEndHour);

      // Enforce minimum duration
      const minEnd = addMinutes(
        dragRef.current.originalStart,
        minDurationMinutes
      );
      if (newEnd < minEnd) {
        newEnd = minEnd;
      }

      setPendingEnd(newEnd);
    }
  };

  // Handle pointer up - apply pending changes
  const handlePointerUp = () => {
    // When moving the block, both times change together - use onRangeChange to avoid race conditions
    if (dragType === "move" && pendingStart && pendingEnd && onRangeChange) {
      onRangeChange(pendingStart, pendingEnd);
    } else {
      // For resize operations or when onRangeChange isn't available
      if (pendingStart) {
        onStartTimeChange(pendingStart);
      }
      if (pendingEnd) {
        onEndTimeChange(pendingEnd);
      }
    }

    dragRef.current = null;
    setIsDragging(false);
    setDragType(null);
    setPendingStart(null);
    setPendingEnd(null);
  };

  // Current time indicator (using centralized helper)
  const currentTimeY = React.useMemo(() => {
    if (!isTodayInTimezone(date, timeZone)) return null;

    // Get current minutes from midnight in office timezone
    const totalMins = getCurrentTimePosition(timeZone);

    // Convert to Y position based on visible range and scale
    const y = (totalMins - visibleStartHour * 60) * pixelsPerMinute;

    if (y < 0 || y > totalHeight) return null;
    return y;
  }, [
    date,
    timeZone,
    visibleStartHour,
    pixelsPerMinute,
    totalHeight,
    currentTime
  ]);

  // Handle preset selection
  const handlePresetSelect = (preset: TimePreset) => {
    // Clear any pending keyboard state
    if (keyboardTimeoutRef.current) {
      clearTimeout(keyboardTimeoutRef.current);
      keyboardTimeoutRef.current = null;
    }
    setKeyboardStart(null);
    setKeyboardEnd(null);

    // Clear any pending drag state
    setPendingStart(null);
    setPendingEnd(null);
    setIsDragging(false);
    setDragType(null);

    let newStart: Date;
    let newEnd: Date;

    if (preset.type === "relative") {
      // Relative to NOW - create timeframe from current time IN OFFICE TIMEZONE
      // Get current minutes from midnight in office timezone
      const totalMinsInOffice = getCurrentTimePosition(timeZone);

      // Create a date on the same day as `date` with the office's current time
      const now = new Date(date);
      now.setHours(0, 0, 0, 0);
      now.setMinutes(totalMinsInOffice);

      newStart = roundToSnap(now, snapMinutes);
      const duration = preset.durationMinutes ?? defaultMeetingDuration;
      newEnd = addMinutes(newStart, duration);
    } else if (preset.type === "dynamic") {
      // Dynamic based on office hours
      const midpoint = Math.floor((officeStartHour + officeEndHour) / 2);

      newStart = new Date(date);
      newEnd = new Date(date);

      if (preset.dynamicType === "morning") {
        // First half of office day
        newStart.setHours(officeStartHour, 0, 0, 0);
        newEnd.setHours(midpoint, 0, 0, 0);
      } else if (preset.dynamicType === "afternoon") {
        // Second half of office day
        newStart.setHours(midpoint, 0, 0, 0);
        newEnd.setHours(officeEndHour, 0, 0, 0);
      } else {
        // Full office day
        newStart.setHours(officeStartHour, 0, 0, 0);
        newEnd.setHours(officeEndHour, 0, 0, 0);
      }
    } else {
      // Absolute preset (legacy)
      newStart = new Date(date);
      newStart.setHours(preset.startHour || 0, preset.startMinute || 0, 0, 0);

      newEnd = new Date(date);
      newEnd.setHours(preset.endHour || 0, preset.endMinute || 0, 0, 0);
    }

    // Use combined callback if available to avoid race conditions
    if (onRangeChange) {
      onRangeChange(newStart, newEnd);
    } else {
      onStartTimeChange(newStart);
      onEndTimeChange(newEnd);
    }
  };

  // Keyboard navigation - 15 minute increments
  const KEYBOARD_STEP_MINUTES = 15;

  const roundToStep = (d: Date, direction: "up" | "down"): Date => {
    const totalMinutes = d.getHours() * 60 + d.getMinutes();
    let newMinutes: number;

    if (direction === "down") {
      // Round up to next step
      newMinutes =
        Math.ceil(totalMinutes / KEYBOARD_STEP_MINUTES) * KEYBOARD_STEP_MINUTES;
      if (newMinutes === totalMinutes) {
        newMinutes += KEYBOARD_STEP_MINUTES;
      }
    } else {
      // Round down to previous step
      newMinutes =
        Math.floor(totalMinutes / KEYBOARD_STEP_MINUTES) *
        KEYBOARD_STEP_MINUTES;
      if (newMinutes === totalMinutes) {
        newMinutes -= KEYBOARD_STEP_MINUTES;
      }
    }

    const result = new Date(d);
    result.setHours(Math.floor(newMinutes / 60), newMinutes % 60, 0, 0);
    return result;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!startTime || !endTime) return;
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;

    e.preventDefault();

    // Use keyboard state if active, otherwise use prop values
    const currentStart = keyboardStart || startTime;
    const currentEnd = keyboardEnd || endTime;

    const isShift = e.shiftKey;
    const isCtrl = e.ctrlKey || e.metaKey;
    const direction = e.key === "ArrowDown" ? "down" : "up";

    let newStart = currentStart;
    let newEnd = currentEnd;

    if (isCtrl) {
      // Move entire block (both start and end) - allow going beyond visible range
      newStart = roundToStep(currentStart, direction);
      newEnd = roundToStep(currentEnd, direction);
      // Don't clamp - allow times to go beyond visible range (next/prev day)
    } else if (isShift) {
      // Change end time
      newEnd = roundToStep(currentEnd, direction);
      newEnd = clampToRange(newEnd, date, visibleStartHour, visibleEndHour);

      // Enforce minimum duration
      const minEnd = addMinutes(currentStart, minDurationMinutes);
      if (newEnd < minEnd) {
        newEnd = minEnd;
      }
    } else {
      // Change start time
      newStart = roundToStep(currentStart, direction);
      newStart = clampToRange(newStart, date, visibleStartHour, visibleEndHour);

      // Enforce minimum duration
      const maxStart = addMinutes(currentEnd, -minDurationMinutes);
      if (newStart > maxStart) {
        newStart = maxStart;
      }
    }

    // Update keyboard state immediately for visual feedback
    setKeyboardStart(newStart);
    setKeyboardEnd(newEnd);

    // Clear existing timeout
    if (keyboardTimeoutRef.current) {
      clearTimeout(keyboardTimeoutRef.current);
    }

    // Debounce the actual onChange calls
    keyboardTimeoutRef.current = setTimeout(() => {
      // Use combined callback if available and both times changed
      if (
        onRangeChange &&
        (newStart !== currentStart || newEnd !== currentEnd)
      ) {
        onRangeChange(newStart, newEnd);
      } else {
        if (newStart !== currentStart) onStartTimeChange(newStart);
        if (newEnd !== currentEnd) onEndTimeChange(newEnd);
      }
      setKeyboardStart(null);
      setKeyboardEnd(null);
    }, 500);
  };

  // Check if a preset is currently selected
  const isPresetSelected = (preset: TimePreset): boolean => {
    if (!startTime || !endTime) return false;

    if (preset.type === "relative") {
      // Relative presets don't have a fixed "selected" state since they're based on NOW
      return false;
    } else if (preset.type === "dynamic") {
      const midpoint = Math.floor((officeStartHour + officeEndHour) / 2);

      if (preset.dynamicType === "morning") {
        return (
          startTime.getHours() === officeStartHour &&
          startTime.getMinutes() === 0 &&
          endTime.getHours() === midpoint &&
          endTime.getMinutes() === 0
        );
      } else if (preset.dynamicType === "afternoon") {
        return (
          startTime.getHours() === midpoint &&
          startTime.getMinutes() === 0 &&
          endTime.getHours() === officeEndHour &&
          endTime.getMinutes() === 0
        );
      } else {
        // Full office day
        return (
          startTime.getHours() === officeStartHour &&
          startTime.getMinutes() === 0 &&
          endTime.getHours() === officeEndHour &&
          endTime.getMinutes() === 0
        );
      }
    } else {
      // Absolute preset
      return (
        startTime.getHours() === preset.startHour &&
        startTime.getMinutes() === preset.startMinute &&
        endTime.getHours() === preset.endHour &&
        endTime.getMinutes() === preset.endMinute
      );
    }
  };

  const timelineContent = (
    <div
      ref={containerRef}
      data-testid="timeline-container"
      className="relative h-full"
      style={{
        touchAction: "none",
        paddingTop: VERTICAL_PADDING,
        paddingBottom: VERTICAL_PADDING
      }}
      onPointerDown={dragToSelect.onBgPointerDown}
      onPointerMove={(e) => {
        dragToSelect.onBgPointerMove(e);
        handlePointerMove(e);
      }}
      onPointerUp={(e) => {
        dragToSelect.onBgPointerUp(e);
        handlePointerUp();
      }}
      onPointerCancel={(e) => {
        dragToSelect.onBgPointerUp(e);
        handlePointerUp();
      }}
    >
      <div className="relative" style={{ height: totalHeight }}>
        {/* Left timeline overlay for drag interaction */}
        <div
          data-testid="left-timeline-overlay"
          className="absolute top-0 bottom-0 left-0 w-12 z-5"
          style={{ touchAction: "none" }}
        />

        {/* Hour markers */}
        {hourMarkers.map((hour) => {
          const y = (hour - visibleStartHour) * 60 * pixelsPerMinute;
          const formattedHour = format(
            new Date().setHours(hour, 0, 0, 0),
            "h a"
          );
          return (
            <div
              key={hour}
              className="absolute left-0 right-0"
              style={{ top: y }}
            >
              <div className="flex items-center">
                <span className="w-12 pr-0.5 text-right text-2xs text-content shrink-0 whitespace-nowrap">
                  {formattedHour}
                </span>
                <div className="flex-1 border-t border-neutral-200" />
              </div>
            </div>
          );
        })}

        {/* Current time indicator */}
        {currentTimeY !== null && (
          <div
            data-testid="now-line"
            className="absolute left-12 right-0 h-0.5 bg-red-500 z-20 pointer-events-none"
            style={{ top: currentTimeY }}
          >
            <div className="absolute -left-1.5 -top-1.5 w-3 h-3 rounded-full bg-red-500" />
          </div>
        )}

        {/* Selection block */}
        {selectionStyle && displayStart && displayEnd && (
          <div
            data-selection-block="true"
            className={cn(
              "absolute left-12 right-1 cursor-grab z-10",
              "bg-accent border border-accent-foreground/20",
              selectionStyle.extendsToNextDay
                ? "rounded-t-md border-b-0"
                : "rounded-md",
              selectionStyle.extendsToPrevDay && "rounded-b-md border-t-0",
              isDragging && dragType === "move" && "cursor-grabbing"
            )}
            style={{
              top: selectionStyle.top,
              height: selectionStyle.height
            }}
            onPointerDown={handleSelectionPointerDown}
          >
            {/* Top resize handle */}
            <div
              data-handle="top"
              className="absolute top-0 left-0 right-0 h-3 cursor-ns-resize hover:bg-accent-foreground/10 rounded-t-md"
            />

            {/* Time display */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-[10px] font-medium text-accent-foreground bg-white/80 px-1.5 py-0.5 rounded whitespace-nowrap">
                {format(displayStart, "h:mm a")} -{" "}
                {format(displayEnd, "h:mm a")}
                {selectionStyle.extendsToNextDay && " +1"}
              </span>
            </div>

            {/* Next day indicator */}
            {selectionStyle.extendsToNextDay && (
              <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-b from-transparent to-accent-foreground/20 flex items-end justify-center pb-0.5">
                <span className="text-[9px] font-medium text-accent-foreground">
                  ↓ next day
                </span>
              </div>
            )}

            {/* Bottom resize handle */}
            <div
              data-handle="bottom"
              className="absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize hover:bg-accent-foreground/10 rounded-b-md"
            />
          </div>
        )}
      </div>
    </div>
  );

  const controlKey = getControlKey();

  // The timeline is a single keyboard-operated control: name it, point it at the
  // shortcuts and announce the range as it changes
  const shortcutsId = React.useId();
  // The committed range, not the drag preview, so dragging is not read out frame by frame
  const rangeAnnouncement =
    startTime && endTime
      ? `${format(startTime, "h:mm a")} to ${format(endTime, "h:mm a")}`
      : "No time selected";
  const groupProps = {
    role: "group",
    "aria-label": "Time range",
    "aria-describedby": shortcutsId
  } as const;
  const liveRange = (
    <span className="sr-only" aria-live="polite">
      {rangeAnnouncement}
    </span>
  );

  // Keyboard shortcut helper
  const kbdClass = "px-1 py-0.5 bg-neutral-100 rounded text-[9px]";
  const Kbd = ({ children }: { children: React.ReactNode }) => (
    <kbd className={kbdClass}>{children}</kbd>
  );

  const shortcuts = [
    { keys: ["↑↓"], label: "Start" },
    { keys: ["⇧", "↑↓"], label: "End" },
    { keys: [controlKey, "↑↓"], label: "Both" },
    {
      keys: [controlKey, "Drag/Click"],
      label: `Snap to ${MODIFIER_SNAP_MINUTES}min`,
      separator: " - "
    }
  ];

  const footer = (
    <div
      id={shortcutsId}
      className="border-t border-neutral-200 pt-2 mt-2 text-[10px] text-content space-y-0.5 max-md:hidden"
    >
      <div className="flex gap-3">
        {shortcuts.map((shortcut, index) => (
          <span key={index}>
            {shortcut.keys.map((key, i) => (
              <React.Fragment key={i}>
                {i > 0 && "+"}
                <Kbd>{key}</Kbd>
              </React.Fragment>
            ))}
            {shortcut.separator || " "}
            {shortcut.label}
          </span>
        ))}
      </div>
    </div>
  );

  if (!showPresets) {
    const wrappedTimelineContent = scrollable ? (
      <ScrollArea
        style={{ height }}
        scrollBarClassname="-mr-1"
        viewportRef={scrollViewportRef}
      >
        {timelineContent}
      </ScrollArea>
    ) : (
      <div style={{ height }}>{timelineContent}</div>
    );

    return (
      <div
        ref={wrapperRef}
        className={cn(
          "relative select-none outline-none flex flex-col w-full min-w-[200px]",
          className
        )}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        {...groupProps}
      >
        {wrappedTimelineContent}
        {footer}
        {liveRange}
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={cn(
        "relative select-none outline-none flex flex-col w-full",
        className
      )}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      {...groupProps}
    >
      {liveRange}
      <div className="flex" style={{ height }}>
        {/* Presets panel */}
        <div className="w-[120px] shrink-0 border-r border-neutral-200 pr-2 flex flex-col gap-1">
          <div className="text-xs font-medium text-content px-2 py-1">
            Quick Select
          </div>
          {presets.map((preset) => {
            const selected = isPresetSelected(preset);
            return (
              <button
                key={preset.label}
                type="button"
                aria-pressed={selected}
                onClick={() => handlePresetSelect(preset)}
                className={cn(
                  "text-left px-2 py-1.5 text-sm rounded-md transition-colors cursor-pointer",
                  selected
                    ? "bg-accent text-accent-foreground font-medium"
                    : "hover:bg-neutral-100 text-foreground"
                )}
              >
                {preset.label}
              </button>
            );
          })}
        </div>

        {/* Timeline panel */}
        <div className="flex-1 min-w-0">
          {scrollable ? (
            <ScrollArea
              style={{ height }}
              viewportClassName="pr-1"
              scrollBarClassname=" w-1"
              viewportRef={scrollViewportRef}
            >
              {timelineContent}
            </ScrollArea>
          ) : (
            timelineContent
          )}
        </div>
      </div>
      {footer}

      {/* Mobile snap toggle - positioned at bottom of container, only visible on mobile */}
      <button
        type="button"
        aria-pressed={mobileSnapEnabled}
        onClick={() => setMobileSnapEnabled(!mobileSnapEnabled)}
        className={cn(
          "md:hidden mt-2 px-3 py-2 text-sm rounded-md transition-colors border-2 border-dashed text-center w-full",
          mobileSnapEnabled
            ? "bg-accent/20 border-accent text-accent-foreground font-medium"
            : "bg-neutral-100 border-neutral-300 text-content hover:bg-neutral-200"
        )}
      >
        {mobileSnapEnabled
          ? `Snap: ${MODIFIER_SNAP_MINUTES}min ✓`
          : `Tap: Snap ${MODIFIER_SNAP_MINUTES}min`}
      </button>
    </div>
  );
};
